<template>
  <div class="address-selector">
    <input
      ref="search"
      type="text"
      class="map-search"
      :placeholder="searchPlaceholder"
    />
    <div ref="map" class="map mt-1 mb-2"></div>
  </div>
</template>

<script>
import GeocodeResult from "../lib/geocode_result.js";

export default {
  props: {
    startingAddress: {
      type: Object,
      default: null
    },
    zoomLevel: {
      type: Number,
      default: 13
    },
    searchPlaceholder: {
      type: String,
      default: "Search for a nearby location"
    },
    showInfoWindow: {
      type: Boolean,
      default: true
    }
  },

  emits: ["update:location"],

  data() {
    return {
      location: null
    };
  },

  computed: {
    startingPosition: function () {
      // Default Dallas, TX
      const lat = this.startingAddress?.latitude || 32.7786;
      const lng = this.startingAddress?.longitude || -96.8045;

      return { lat, lng };
    }
  },

  watch: {
    startingAddress: function () {
      this.resetMap();
    }
  },

  mounted() {
    this.map = this.initializeMap();
    this.searchBox = this.initializeSearchBox();
    this.infoWindow = this.initializeInfoWindow();
    this.marker = this.initializeMarker();
    this.geocoder = this.initializeGeocoder();

    this.map.addListener("click", this.updateMarker);
    this.map.addListener("click", this.updateLocation);
    this.searchBox.addListener("places_changed", this.updateMap);

    this.displayOverlays();
  },

  methods: {
    initializeMap() {
      const map = new google.maps.Map(this.$refs.map, {
        center: this.startingPosition,
        zoom: this.zoomLevel,
        draggableCursor: "default",
        draggingCursor: "move"
      });

      return map;
    },
    initializeSearchBox() {
      const searchBox = new google.maps.places.SearchBox(this.$refs.search);

      this.map.controls[google.maps.ControlPosition.TOP_LEFT].push(
        this.$refs.search
      );

      return searchBox;
    },
    initializeInfoWindow() {
      return new google.maps.InfoWindow({
        content: "Click the map to pinpoint the location"
      });
    },
    initializeMarker() {
      return new google.maps.Marker({ title: "Address Location" });
    },
    initializeGeocoder() {
      return new google.maps.Geocoder();
    },
    displayOverlays() {
      if (this.showInfoWindow) {
        this.marker.setMap(null);
        this.infoWindow.setPosition(this.startingPosition);
        this.infoWindow.open({ map: this.map, shouldFocus: false });
      } else {
        this.updateMarker({ latLng: this.startingPosition });
      }
    },
    // Update the map boundaries based on the search box selection
    updateMap() {
      const places = this.searchBox.getPlaces();

      if (places.length == 0) {
        return;
      }

      const bounds = new google.maps.LatLngBounds();
      places.forEach((place) => {
        if (!place.geometry || !place.geometry.location) {
          return;
        }

        if (place.geometry.viewport) {
          bounds.union(place.geometry.viewport);
        } else {
          bounds.extend(place.geometry.location);
        }
        this.updateMarker({ latLng: place.geometry.location }, true);
      });
      this.map.fitBounds(bounds);
    },
    updateMarker(event, updateLocation = false) {
      this.infoWindow.close();
      this.marker.setPosition(event.latLng);
      this.marker.setMap(this.map);
      if (updateLocation) {
        this.updateLocation(event);
      }
    },
    async updateLocation(event) {
      const response = await this.geocoder.geocode({ location: event.latLng });
      this.location = new GeocodeResult(response.results[0]);
      this.$emit("update:location", this.location);
    },
    resetMap() {
      this.map.setCenter(this.startingPosition);
      this.map.setZoom(this.zoomLevel);
      this.displayOverlays();
      this.$refs.search.value = "";
      this.location = null;
    }
  }
};
</script>

<style>
.map {
  position: relative;
  height: 320px;
  width: 100%;
}
.map-search {
  border: none;
  border-radius: 2px;
  box-shadow: rgba(0, 0, 0, 0.3) 0px 1px 4px -1px;
  width: 300px;
  height: 40px;
  margin-top: 10px;
  font-size: 14px;
  padding: 10px;
}
/*
Fix for blue border around map on Chrome.
See here: https://stackoverflow.com/questions/66669173/google-maps-js-api-shows-blue-focus-border
 */
.map .gm-style iframe + div {
  border: none !important;
}
</style>
